import { useTranslation } from 'react-i18next';
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { GroupInfosManagement } from '../components/GroupInfosManagement';
import { useGetGroupById, useUpdateGroup } from '@/hooks/group.hooks';
import { groupSchema, TGroupSchema } from '@/schemas/group/group.schema';
import { GroupConfigsManagement } from '../components/GroupConfigsManagement';
import { GroupReportManagement } from '../components/GroupReportManagement';
import { IconTransfer } from '@tabler/icons-react';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { EntityRelationshipsManagement } from '@/components/EntityRelationshipsManagement';
import { TCompanySchema } from '@/schemas/company/company.schema';
import {
  useGetAssignedCompaniesToGroup,
  useGetUnassignedCompaniesToGroup,
  useUpdateCompany,
} from '@/hooks/company.hooks';
import { LUCIUS_GROUP_ID } from '@/api/entities/group';
import { GroupWhiteLabelManagement } from '../components/GroupWhiteLabelManagement';
import { useListAllEquipments } from '@/hooks/equipment.hooks';
import { Equipment } from '@/api/entities/equipment';
import { useGetCheckPointsBygroupId } from '@/hooks/check-point.hooks';
import { useGetCheckpointGroupById } from '@/hooks/checkpoint-group.hooks';
import { CheckPoint } from '@/api/entities/check-point';
import { useGetTab } from '@/hooks/tab';
import { EditGroupTab } from '@/type/ui/tab/groupTab';
import { useUserRole } from '@/hooks/auth.hooks';
import { getLanguage } from '@/utils/language.utils';
import { GroupAPIManagement } from '../components/GroupAPIManagement';

export const EditGroupPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('group');
  const { data, isLoading } = useGetGroupById(`${id}`);
  const navigate = useNavigate();

  const { isModerator } = useUserRole();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditGroupTab.INFOS,
  });

  const {
    watch: watchGroup,
    control: groupControl,
    register: registerGroup,
    setValue: setGroupValue,
    setError: setGroupError,
    handleSubmit: handleSubmitGroup,
    formState: { errors: groupErrors, isSubmitting: isSubmittingGroup },
  } = useForm<TGroupSchema>({
    resolver: zodResolver(groupSchema),
  });

  const { mutate: mutateUpdateGroup, isPending: isPendingUpdateGroup } =
    useUpdateGroup({
      t,
      handleSuccess: () => navigate('/groups'),
    });

  const { data: equipments } = useListAllEquipments();
  const { data: checkpointGroup } = useGetCheckpointGroupById(id);
  const { data: checkpoints } = useGetCheckPointsBygroupId(id);

  const isWhiteLabel = data?.data?.isWhiteLabel;

  const onSubmitGroupInfos = (group: TGroupSchema) => {
    const { languageCode } = getLanguage();

    const groupWithId = {
      id,
      ...group,
      distributorId: group.distributorId || LUCIUS_GROUP_ID,
      languageCode,
    };
    mutateUpdateGroup(groupWithId);
  };

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/groups'}
        title={t('theGroup')}
        subTitle={data?.data?.name}
        subTitleIconClassName="ki-abstract-45"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('groupList')} linkTo="/groups" />
            <BreadcrumbItem text={data?.data?.name} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('groupNotFound')}...`} />
      ) : (
        <div>
          {!isModerator && (
            <TabList className="mb-5">
              <TabItem
                text={t('common:informations')}
                leftIcon={<i className="ki-outline ki-badge" />}
                active={currentTab === EditGroupTab.INFOS}
                onChange={() => navigateToTab(EditGroupTab.INFOS)}
              />
              <TabItem
                text={t('common:report')}
                leftIcon={<i className="ki-outline ki-questionnaire-tablet" />}
                active={currentTab === EditGroupTab.REPORT}
                onChange={() => navigateToTab(EditGroupTab.REPORT)}
              />
              <TabItem
                text={t('common:settings')}
                leftIcon={<i className="ki-outline ki-setting-4" />}
                active={currentTab === EditGroupTab.CONFIGS}
                onChange={() => navigateToTab(EditGroupTab.CONFIGS)}
              />
              <TabItem
                text={t('linkedCompanies')}
                leftIcon={<i className="ki-outline ki-note-2" />}
                active={currentTab === EditGroupTab.LINKED_COMPANIES}
                onChange={() => navigateToTab(EditGroupTab.LINKED_COMPANIES)}
              />
              {isWhiteLabel && (
                <TabItem
                  text={t('common:whiteLabel')}
                  leftIcon={<i className="ki-outline ki-copy-success" />}
                  active={currentTab === EditGroupTab.WHITE_LABEL}
                  onChange={() => navigateToTab(EditGroupTab.WHITE_LABEL)}
                />
              )}
              <TabItem
                text="Api"
                leftIcon={<IconTransfer stroke={2} size={18} />}
                active={currentTab === EditGroupTab.API}
                onChange={() => navigateToTab(EditGroupTab.API)}
              />
            </TabList>
          )}
          {currentTab === EditGroupTab.INFOS && (
            <GroupInfosManagement<TGroupSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchGroup}
              control={groupControl}
              register={registerGroup}
              setValue={setGroupValue}
              setError={setGroupError}
              errors={groupErrors}
              submitDisabled={isSubmittingGroup || isPendingUpdateGroup}
              onSubmit={handleSubmitGroup(onSubmitGroupInfos)}
            />
          )}
          {currentTab === EditGroupTab.REPORT && (
            <GroupReportManagement
              t={t}
              checkPoints={checkpoints?.data as CheckPoint[]}
              equipments={equipments?.data as Equipment[]}
              checkpointGroup={checkpointGroup?.data}
              data={!isLoading && data?.data}
              watch={watchGroup}
              control={groupControl}
              register={registerGroup}
              setValue={setGroupValue}
              setError={setGroupError}
              errors={groupErrors}
              submitDisabled={isSubmittingGroup || isPendingUpdateGroup}
              onSubmit={handleSubmitGroup(onSubmitGroupInfos)}
            />
          )}
          {currentTab === EditGroupTab.CONFIGS && (
            <GroupConfigsManagement<TGroupSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchGroup}
              control={groupControl}
              register={registerGroup}
              setValue={setGroupValue}
              setError={setGroupError}
              errors={groupErrors}
              submitDisabled={isSubmittingGroup || isPendingUpdateGroup}
              onSubmit={handleSubmitGroup(onSubmitGroupInfos)}
            />
          )}
          {currentTab === EditGroupTab.LINKED_COMPANIES && (
            <EntityRelationshipsManagement<TCompanySchema>
              t={t}
              foreignKeyId={id}
              foreignKeyField="groupId"
              title={t('companiesLinkedToThisGroup')}
              searchText={t('common:searchCompany')}
              mainContentNotFoundText={t('noCompanyLinkedToThisGroup')}
              addButtonText={t('common:addCompany')}
              modalTitle={`${t('selectOneOrManyCompaniesBelow')} :`}
              modalDataNotFoundText={t('noCompanyAvailable')}
              useGetUnassignedEntities={useGetUnassignedCompaniesToGroup}
              useGetAssignedEntities={useGetAssignedCompaniesToGroup}
              useUpdateEntity={useUpdateCompany}
            />
          )}
          {currentTab === EditGroupTab.WHITE_LABEL && (
            <GroupWhiteLabelManagement<TGroupSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchGroup}
              control={groupControl}
              register={registerGroup}
              setValue={setGroupValue}
              setError={setGroupError}
              errors={groupErrors}
              submitDisabled={isSubmittingGroup || isPendingUpdateGroup}
              onSubmit={handleSubmitGroup(onSubmitGroupInfos)}
            />
          )}
          {currentTab === EditGroupTab.API && (
            <GroupAPIManagement apiToken={data?.data.apiToken} t={t} />
          )}
        </div>
      )}
      {(isLoading || isSubmittingGroup || isPendingUpdateGroup) && <Loader />}
    </>
  );
};
